import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import LoadingButton from "@material-ui/lab/LoadingButton";
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import { useSnackbar } from "src/components/snackbar";
import SelectMenu from "../../SelectMenu";
import { ajustesApi } from "src/services/ajustesApi";
import { useActualizarDatosPersonalesClienteMutation } from "src/services/accesoClientesApi";
import {
  cardStyles,
  UI_COLORS,
} from "../../cliente/styles/clientePortalStyles";

// ─── Constantes ──────────────────────────────────────────────────────────────
// Regex flexible: acepta dígitos, +, espacios, guión, paréntesis, puntos
const TELEFONO_REGEX = /^[+\d][\d\s\-(). ]{6,19}$/;

// ─── Sub-componentes ─────────────────────────────────────────────────────────
const SectionHeader = ({ title }) => (
  <Box sx={{ mt: 3, mb: 2 }}>
    <Typography
      variant="caption"
      sx={{
        display: "block",
        fontWeight: 700,
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        mb: 1,
      }}
    >
      {title}
    </Typography>
    <Divider />
  </Box>
);

const readOnlySx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: UI_COLORS.background.default,
  },
};

// ─── Validación Yup ──────────────────────────────────────────────────────────
const validationSchema = Yup.object().shape({
  nombre: Yup.string().required("El nombre es requerido"),
  correo: Yup.string()
    .email("El correo ingresado no es válido")
    .required("El correo es requerido"),
  correoFacturacion: Yup.string()
    .email("El correo de facturación no es válido")
    .required("El correo de facturación es requerido"),
  telefono: Yup.string()
    .matches(
      TELEFONO_REGEX,
      "Teléfono inválido. Puede incluir dígitos, +, espacios, guiones y paréntesis",
    )
    .required("El teléfono es requerido"),
  contacto: Yup.string().required("El contacto es requerido"),
  direccion: Yup.string().required("La dirección es requerida"),
  comuna: Yup.string().required("La comuna es requerida"),
  gIRO: Yup.string().nullable(),
});

// ─── Componente principal ─────────────────────────────────────────────────────
export default function DatosPersonalesClienteSeleccionado({ value, cliente }) {
  const { enqueueSnackbar } = useSnackbar();

  // ─── RTK Query ───────────────────────────────────────────────────────────────
  const { data: comunas = [] } = ajustesApi.useGetComunasQuery();
  const { data: girosData = [] } =
    ajustesApi.useGetSelectorElementoQuery(1813328154);
  const [actualizarDatosPersonalesCliente] =
    useActualizarDatosPersonalesClienteMutation();
  // ─────────────────────────────────────────────────────────────────────────────

  const giros = girosData.map((g) => g.nombreElemento);

  // ─── Formik ──────────────────────────────────────────────────────────────────
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      rut: cliente?.rut || "",
      nombre: cliente?.nombre || "",
      gIRO: cliente?.gIRO || "",
      grupo: cliente?.grupo || "",
      telefono: cliente?.telefono || "",
      correo: cliente?.correo || "",
      correoFacturacion: cliente?.correoFacturacion || "",
      contacto: cliente?.contacto || "",
      direccion: cliente?.direccion || "",
      comuna: cliente?.comuna || "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const datos = {
          nombre: values.nombre,
          gIRO: values.gIRO || null,
          telefono: values.telefono,
          correo: values.correo,
          correoFacturacion: values.correoFacturacion,
          contacto: values.contacto,
          direccion: values.direccion,
          comuna: values.comuna,
        };
        const res = await actualizarDatosPersonalesCliente({
          rut: cliente.rut,
          datos,
        }).unwrap();
        enqueueSnackbar(res?.message || "Datos actualizados correctamente", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
      } catch (error) {
        enqueueSnackbar(
          error?.data?.message || "Error al actualizar los datos",
          {
            variant: "error",
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
          },
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setFieldValue,
  } = formik;
  // ─────────────────────────────────────────────────────────────────────────────

  if (value !== 5) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Card variant="outlined" sx={{ ...cardStyles, borderRadius: 0 }}>
        <CardContent sx={{ px: 3, pt: 3 }}>
          {/* ── Información General ── */}
          <SectionHeader title="Información General" />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="RUT"
                disabled
                variant="outlined"
                sx={readOnlySx}
                {...getFieldProps("rut")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre"
                disabled
                variant="outlined"
                sx={readOnlySx}
                {...getFieldProps("nombre")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: -2 }}>
                <SelectMenu
                  options={giros}
                  label="GIRO"
                  value={values.gIRO || null}
                  onChange={(val) => setFieldValue("gIRO", val ?? "")}
                />
              </Box>
            </Grid>
          </Grid>

          {/* ── Información de Contacto ── */}
          <SectionHeader title="Información de Contacto" />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono"
                variant="outlined"
                placeholder="Ej: +56 9 1234 5678"
                {...getFieldProps("telefono")}
                error={Boolean(touched.telefono && errors.telefono)}
                helperText={
                  (touched.telefono && errors.telefono) || "Ej: +56 9 1234 5678"
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Correo Personal"
                variant="outlined"
                {...getFieldProps("correo")}
                error={Boolean(touched.correo && errors.correo)}
                helperText={touched.correo && errors.correo}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Correo Facturación"
                variant="outlined"
                {...getFieldProps("correoFacturacion")}
                error={Boolean(
                  touched.correoFacturacion && errors.correoFacturacion,
                )}
                helperText={
                  touched.correoFacturacion && errors.correoFacturacion
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contacto"
                variant="outlined"
                {...getFieldProps("contacto")}
                error={Boolean(touched.contacto && errors.contacto)}
                helperText={touched.contacto && errors.contacto}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Dirección"
                variant="outlined"
                {...getFieldProps("direccion")}
                error={Boolean(touched.direccion && errors.direccion)}
                helperText={touched.direccion && errors.direccion}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: -2 }}>
                <SelectMenu
                  options={comunas.map((c) => c.nombre)}
                  label="Seleccione Comuna"
                  value={values.comuna || null}
                  onChange={(val) => setFieldValue("comuna", val ?? "")}
                />
                {touched.comuna && errors.comuna && (
                  <Typography
                    color="error"
                    variant="caption"
                    style={{
                      marginLeft: "8px",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    {errors.comuna}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>

        <CardActions sx={{ justifyContent: "center", pb: 3, px: 3 }}>
          <LoadingButton
            size="large"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={handleSubmit}
            sx={{ minWidth: 180 }}
          >
            Guardar Cambios
          </LoadingButton>
        </CardActions>
      </Card>
    </Box>
  );
}
